import { FC, type JSX } from 'react';

import ProductIcon from '@eneco/components/ProductIcon/ProductIcon';
import RichText from '@eneco/components/RichText/RichText';
import { productUnits } from '@eneco/enumerations/productUnits';
import { useFormatter } from '@eneco/i18n';
import { Box, NotificationBox, Stack, Text } from '@eneco/sparky';
import { Fields } from '@sitecore/types/OfferStep';

import { FlowConsumerContextEnergy } from '../../../context';
import { constructTextualDataKey } from '../../../shared/helpers';
import { useProductItemContent } from '../../../shared/hooks';
import { Offer , OfferType } from '../../../shared/types';

interface Props {
  offer?: Offer;
  textualData?: Fields;
  flowContext?: FlowConsumerContextEnergy;
}

export const TariffDetailsOverview: FC<Props> = ({ textualData, offer, flowContext }): JSX.Element => {
  const { energyType, isSME } = flowContext ?? {};
  const { costDetails, usages, discountType } = offer ?? {};

  const {
    electricityDeliveryNormal = 0,
    electricityDeliveryLow = 0,
    gas = 0,
    warmthElectricity = 0,
    warmthWater = 0,
  } = usages || {};

  const { currency } = useFormatter();

  const costsType = isSME ? 'vatExcluded' : 'vatIncluded';

  const textualDataKey = constructTextualDataKey(offer?.contractSpan, energyType);
  const { getProductDetails } = useProductItemContent(textualData?.productData?.productDataList);

  const productDetails = getProductDetails(textualDataKey);

  const tariffDescriptionText = productDetails?.tariffDescription;
  const tariffSplitExplanationText = productDetails?.tariffSplitExplanation;

  const shouldShowVariablePricingNotification =
    discountType?.includes(OfferType.Variable) &&
    !!textualData?.productData?.variableProductNotification?.value?.content;

  return (
    <Text size={{ initial: 'BodyXS', md: 'BodyS' }}>
      <Stack gap={{ initial: '1', md: '3' }}>
        {shouldShowVariablePricingNotification && (
          <NotificationBox
            isAlert={false}
            variant={textualData?.productData?.variableProductNotification?.value?.variant}
            title={textualData?.productData?.variableProductNotification?.value?.title}
            text={<RichText html={textualData?.productData?.variableProductNotification?.value?.content} />}
          />
        )}
        {(electricityDeliveryNormal || electricityDeliveryLow) && (
          <>
            <Stack.Item>
              <Stack direction="row" alignY="start" gap="2">
                <Stack.Item grow={true}>
                  <Stack direction="row" alignY="center" gap="2">
                    <ProductIcon type="electricity" />
                    <Text weight="bold">
                      {Number(electricityDeliveryLow) > 0
                        ? productDetails?.unitPriceLabels?.electricityHighLabel ||
                          textualData?.unitPriceLabels?.electricityHighLabel?.value
                        : productDetails?.unitPriceLabels?.electricityLabel ||
                          textualData?.unitPriceLabels?.electricityLabel?.value}
                    </Text>
                  </Stack>
                </Stack.Item>

                <Stack.Item shrink={false}>
                  <Stack direction="row" alignY="end" gap="2">
                    <Stack.Item grow={true}>
                      {`${currency.euroHighPrecision(
                        costDetails?.electricityCostDetails?.tariff?.price?.[costsType],
                      )} / ${productUnits.electricity}`}
                    </Stack.Item>
                  </Stack>
                </Stack.Item>
              </Stack>
            </Stack.Item>
            {Number(electricityDeliveryLow) > 0 && (
              <Stack.Item>
                <Stack direction="row" alignY="start" gap="2">
                  <Stack.Item grow={true}>
                    <Stack direction="row" alignY="center" gap="2">
                      <ProductIcon type="electricity" />
                      <Text weight="bold">
                        {productDetails?.unitPriceLabels?.electricityLowLabel ||
                          textualData?.unitPriceLabels?.electricityLowLabel?.value}
                      </Text>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item shrink={false}>
                    <Stack direction="row" alignY="end" gap="2">
                      <Stack.Item grow={true}>
                        {`${currency.euroHighPrecision(
                          costDetails?.electricityCostDetails?.lowtariff?.price?.[costsType],
                        )} / ${productUnits.electricity}`}
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                </Stack>
              </Stack.Item>
            )}
          </>
        )}
        {Number(gas) > 0 && (
          <Stack.Item>
            <Stack direction="row" alignY="start" gap="2">
              <Stack.Item grow={true}>
                <Stack direction="row" alignY="center" gap="2">
                  <ProductIcon type="gas" />
                  <Text weight="bold">
                    {productDetails?.unitPriceLabels?.gasLabel || textualData?.unitPriceLabels?.gasLabel?.value}
                  </Text>
                </Stack>
              </Stack.Item>

              <Stack.Item shrink={false}>
                <Stack direction="row" alignY="end" gap="2">
                  <Stack.Item grow={true}>
                    {`${currency.euroHighPrecision(costDetails?.gasCostDetails?.tariff?.price?.[costsType])} / ${
                      productUnits.gas
                    }`}
                  </Stack.Item>
                </Stack>
              </Stack.Item>
            </Stack>
          </Stack.Item>
        )}
        {Number(warmthElectricity) > 0 && (
          <Stack.Item>
            <Stack direction="row" alignY="start" gap="2">
              <Stack.Item grow={true}>
                <Stack direction="row" alignY="center" gap="2">
                  <ProductIcon type="warmth" />
                  <Text weight="bold">
                    {productDetails?.unitPriceLabels?.warmthLabel || textualData?.unitPriceLabels?.warmthLabel?.value}
                  </Text>
                </Stack>
              </Stack.Item>

              <Stack.Item shrink={false}>
                <Stack direction="row" alignY="end" gap="2">
                  <Stack.Item grow={true}>
                    {`${currency.euroHighPrecision(
                      costDetails?.warmthEkvCostDetails?.tariff?.price?.[costsType] ??
                        costDetails?.warmthCostDetails?.tariff?.price?.[costsType],
                    )} / ${productUnits.warmth}`}
                  </Stack.Item>
                </Stack>
              </Stack.Item>
            </Stack>
          </Stack.Item>
        )}
        {Number(warmthWater) > 0 && (
          <>
            <Stack.Item>
              <Stack direction="row" alignY="start" gap="2">
                <Stack.Item grow={true}>
                  <Stack direction="row" alignY="center" gap="2">
                    <ProductIcon type="water" />
                    <Text weight="bold">
                      {productDetails?.unitPriceLabels?.waterColdLabel ||
                        textualData?.unitPriceLabels?.waterColdLabel?.value}
                    </Text>
                  </Stack>
                </Stack.Item>

                <Stack.Item shrink={false}>
                  <Stack direction="row" alignY="end" gap="2">
                    <Stack.Item grow={true}>
                      {`${currency.euroHighPrecision(
                        costDetails?.tapwaterCostDetails?.tariffcoldwater?.price?.[costsType],
                      )} / ${productUnits.tapWater}`}
                    </Stack.Item>
                  </Stack>
                </Stack.Item>
              </Stack>
            </Stack.Item>

            <Stack.Item>
              <Stack direction="row" alignY="start" gap="2">
                <Stack.Item grow={true}>
                  <Stack direction="row" alignY="center" gap="2">
                    <ProductIcon type="tapwater" />
                    <Text weight="bold">
                      {productDetails?.unitPriceLabels?.waterWarmLabel ||
                        textualData?.unitPriceLabels?.waterWarmLabel?.value}
                    </Text>
                  </Stack>
                </Stack.Item>

                <Stack.Item shrink={false}>
                  <Stack direction="row" alignY="end" gap="2">
                    <Stack.Item grow={true}>
                      {`${currency.euroHighPrecision(
                        costDetails?.tapwaterCostDetails?.tariffwarmwater?.price?.[costsType],
                      )} / ${productUnits.tapWater}`}
                    </Stack.Item>
                  </Stack>
                </Stack.Item>
              </Stack>
            </Stack.Item>
          </>
        )}
        {costDetails?.electricityCostDetails?.redeliverytariff?.price?.[costsType] && (
          <Stack.Item>
            <Stack direction="row" alignY="start" gap="2">
              <Stack.Item grow={true}>
                <Stack direction="row" alignY="center" gap="2">
                  <ProductIcon type="redelivery" />
                  <Text weight="bold">
                    {productDetails?.unitPriceLabels?.redeliveryLabel ||
                      textualData?.unitPriceLabels?.redeliveryLabel?.value}
                  </Text>
                </Stack>
              </Stack.Item>

              <Stack.Item shrink={false}>
                <Stack direction="row" alignY="end" gap="2">
                  <Stack.Item grow={true}>
                    {`${currency.euroHighPrecision(
                      costDetails?.electricityCostDetails?.redeliverytariff?.price?.[costsType],
                    )} / ${productUnits.electricity}`}
                  </Stack.Item>
                </Stack>
              </Stack.Item>
            </Stack>
          </Stack.Item>
        )}
        {/* TODO - uncomment it when DC part is also ready, we want to introduce it in one-go */}
        {offer?.productFutureRate && (
          <Box paddingTop={{ initial: '3', md: '0' }}>
            <Text size="BodyXS" color="textLowEmphasis">
              {tariffSplitExplanationText}
            </Text>
          </Box>
        )}
        {tariffDescriptionText && (
          <Box paddingTop={{ initial: '3', md: '0' }}>
            <Text size="BodyXS" color="textLowEmphasis">
              {tariffDescriptionText}
            </Text>
          </Box>
        )}
      </Stack>
    </Text>
  );
};
